import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLocation } from "react-router-dom";
import { getTemplateDetail, submitTestimonial, getTestimonialDetail, updateTestimonial, deleteTestimonial } from "../../../utils/testimonialsApi";

const ContentTab = ({ isTemplateId, updateTestimonialTabData,testimonialTabData,templateType }) => {
    const [activeSettingElementsTab, setActiveSettingElementsTab] = useState("main");
    const [apiTestimonials, setApiTestimonials] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [openDropdown, setOpenDropdown] = useState(null);
    const dropdownRefs = useRef([]);
    const [isSubmitTestimonial, setIsSubmitTestimonial] = useState(false);
    const [rating, setRating] = useState(0);
    const location = useLocation();
    const isEditPage = location.pathname.startsWith("/customer/edit/testimonials/");
    const { uuid } = useParams();
    const [templateId, setTemplateId] = useState(() => (isEditPage && uuid ? uuid : isTemplateId || ""));
    const [apiSingleTestimonial, setApiSingleTestimonial] = useState(null);
    const [testimonialId, setTestimonialId] = useState(null);

    //Wrap fetchTemplateDetails in useCallback to prevent re-creation on every render
    const fetchTemplateDetails = useCallback(async () => {
        if (!templateId) return;

        setIsLoading(true);
        try {
            const templateResult = await getTemplateDetail(templateId);
            if (templateResult?.status === true && Array.isArray(templateResult?.data?.testimonial_list)) {
                if(templateType === "Customer_Story_Temp"){
                    const firstTestimonial = templateResult.data.testimonial_list[0]; 
                    if (firstTestimonial) {
                        setApiTestimonials([firstTestimonial]); 
                    } else {
                        setApiTestimonials([]);
                    }
                } else {
                    if (templateResult?.status === true && templateResult?.data?.testimonial_list) {
                        setApiTestimonials(templateResult.data.testimonial_list);
                    } else {
                        setApiTestimonials([]);
                    }
                }
            } else {
                setApiTestimonials([]);
            }
        } catch (error) {
            //console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [templateId,templateType]);

    // Fix useEffect by including fetchTemplateDetails
    useEffect(() => {
        fetchTemplateDetails();
    }, [fetchTemplateDetails]);

    // Also include fetchTemplateDetails in this useEffect
    useEffect(() => {
        if (isEditPage && uuid) {
            setTemplateId(uuid);
        } else {
            setTemplateId(isTemplateId || "");
        }

        fetchTemplateDetails();
    }, [isEditPage, uuid, isTemplateId, fetchTemplateDetails]);

    // Function to handle star rating selection
    const handleStarClick = (value) => {
        setRating(value);
    };

    // Toggle dropdown visibility
    const toggleDropdown = (index) => {
        setOpenDropdown((prev) => (prev === index ? null : index));
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                openDropdown !== null &&
                dropdownRefs.current[openDropdown] &&
                !dropdownRefs.current[openDropdown].contains(event.target) &&
                !event.target.closest(".inner-dropdown-menu")
            ) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openDropdown]);



    //Submit testimonials Handler
    const handleSubmitTestimonial = async (e) => {
        e.preventDefault();

        try {
            setIsSubmitTestimonial(true);

            // Create FormData object
            const formData = new FormData();
            formData.append("template_id", templateId);
            formData.append("name", e.target.name.value);
            formData.append("caption_name", e.target.caption_name.value);
            formData.append("description", e.target.description.value);
            formData.append("rating", e.target.rating.value);
            formData.append("image", e.target.image.files[0]);
            formData.append("website_logo", e.target.website_logo.files[0]);
            formData.append("website_url", e.target.website_url.value);
            formData.append("profile_url", e.target.profile_url.value);
            formData.append("type", "Dynamic");

            //Api Call
            const response = await submitTestimonial(formData);

            if (response.status) {
                toast.success(response.message);

                // Refresh testimonials
                await fetchTemplateDetails();

                // Switch back to the main view
                setActiveSettingElementsTab("main");
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsSubmitTestimonial(false);
        }
    };

    // Handle Edit Click 
    const handleEditTestimonial = (testimonial) => {
        setOpenDropdown(null);
        setTestimonialId(testimonial.id);
        setActiveSettingElementsTab("edit_testimonial");
        fetchTestimonialDetails(testimonial.id);
    };

    // Handle Delete Click
    const handleDeleteTestimonial = async (testimonial) => {
        setOpenDropdown(null);

        try {
            // Prepare the payload
            const payload = {
                testimonial_id: testimonial.id,
            };

            // API Call
            const api_response = await deleteTestimonial(payload);
            // Check API Response
            if (api_response.status === true) {
                toast.success(api_response.message);

                // Refresh testimonials
                await fetchTemplateDetails();
            } else {
                toast.error(api_response.message);
            }
        } catch (error) {
            toast.error(`${error.message}`);
        }
    };

    // Wrap fetchTestimonialDetails in useCallback to prevent re-creation on every render
    const fetchTestimonialDetails = useCallback(async (testimonialId) => {
        if (!testimonialId) return;

        setIsLoading(true);
        try {
            const templateResult = await getTestimonialDetail(testimonialId);
            if (templateResult?.status === true && templateResult?.data) {
                setApiSingleTestimonial(templateResult.data); // Store single testimonial details
            } else {
                setApiSingleTestimonial(null);
            }
        } catch (error) {
            //console.error("Error fetching testimonial:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    //Update testimonials Handler
    const handleUpdateTestimonial = async (e) => {
        e.preventDefault();

        try {
            setIsSubmitTestimonial(true);
            setOpenDropdown(null);
            // Create FormData object
            const formData = new FormData();
            formData.append("id", testimonialId);
            formData.append("name", e.target.name.value);
            formData.append("caption_name", e.target.caption_name.value);
            formData.append("description", e.target.description.value);
            formData.append("rating", e.target.rating.value);
            formData.append("image", e.target.image.files[0]);
            formData.append("website_logo", e.target.website_logo.files[0]);
            formData.append("website_url", e.target.website_url.value);
            formData.append("profile_url", e.target.profile_url.value);

            //Api Call
            const response = await updateTestimonial(formData);

            if (response.status) {
                toast.success(response.message);

                // Refresh testimonials
                await fetchTemplateDetails();

                // Switch back to the main view
                setActiveSettingElementsTab("main");
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsSubmitTestimonial(false);
        }
    };

    return (
        <div className="testimonials-side-baar-content-tab">
            {activeSettingElementsTab === "main" && (
                <>
                    <span>Content</span>
                    <div className="testimonal-side-baar">
                        <h6>Testimonials</h6>
                        <div className="add-testimonal-button">
                            <button onClick={() => setActiveSettingElementsTab("add_testimonial")}>+ Add Testimonial</button>
                        </div>
                        <div className="testimonal-side-baar-list">
                            {isLoading ? (
                                <div className="inner-loader-container">
                                    <div className="inner-spinner"></div>
                                    <p>Loading, please wait...</p>
                                </div>
                            ) : apiTestimonials.length > 0 ? (
                                <ul>
                                    {apiTestimonials.map((testimonial, index) => (
                                        <li key={index} className="testimonial-item">
                                            <button type="button" className="testimonial-item-button" onClick={() => handleEditTestimonial(testimonial)}>
                                                <div className="list-image-testimonal">
                                                    <img src={testimonial.image} alt={testimonial.name} />
                                                </div>
                                                <div className="list-text-testimonal">
                                                    <span>{testimonial.name}</span>
                                                    <p>{testimonial.description}</p>
                                                </div>
                                            </button>
                                            <div className="list-button-testimonal" ref={(el) => (dropdownRefs.current[index] = el)}>
                                                <button onClick={() => toggleDropdown(index)}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M3.864 8.333a1.363 1.363 0 1 1 0 2.726 1.363 1.363 0 0 1 0-2.726Zm6.136 0a1.363 1.363 0 1 1 .001 2.726A1.363 1.363 0 0 1 10 8.333Zm6.136 0a1.363 1.363 0 1 1 .001 2.726 1.363 1.363 0 0 1 0-2.726Z"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                            {openDropdown === index && (
                                                <div className="inner-dropdown-menu">
                                                    <div className="button-list">
                                                        <button type="button" onClick={() => handleEditTestimonial(testimonial)}>Edit</button>
                                                    </div>
                                                    <div className="button-list">
                                                        <button type="button" onClick={() => handleDeleteTestimonial(testimonial)}> Delete</button>
                                                    </div>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p>No testimonials available.</p>
                            )}
                        </div>
                    </div>
                </>
            )}

            {activeSettingElementsTab === "add_testimonial" && (
                <>
                    <div className="inner-box-layout">
                        <form onSubmit={handleSubmitTestimonial}>
                            <span>
                                <button className="button-color" type="button" onClick={() => setActiveSettingElementsTab("main")}>
                                    <i className="fa fa-angle-left" aria-hidden="true"></i>Cancel
                                </button>
                                Add Testimonial
                                <button type="submit" className="button-color button-done" disabled={isSubmitTestimonial}>
                                    {isSubmitTestimonial ? "Submitting..." : "Done"}
                                </button>

                            </span>

                            <div className="side-baar-box-header">
                                <div className="box-header-row">
                                    <textarea name="description" placeholder="Testimonial Text...." className="testi-from"></textarea>
                                </div>
                                <div className="box-header-row">
                                    <span>Star Rating</span>
                                    <div className="start-rating">
                                        {[1, 2, 3, 4, 5].map((value) => (
                                            <i
                                                key={value}
                                                className={`fa fa-star ${value <= rating ? "selected" : ""}`}
                                                aria-hidden="true"
                                                onClick={() => handleStarClick(value)}
                                                style={{ cursor: "pointer", color: value <= rating ? "gold" : "gray" }}
                                            ></i>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {/* Hidden input field to send rating */}
                            <input type="hidden" name="rating" value={rating} />

                            <div className="name-tile-row">Chat Window</div>
                            <div className="side-baar-box-header">
                                <div className="box-header-row bubble-text">
                                    <input name="name" placeholder="Name" type="text" />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <input name="caption_name" placeholder="Caption" type="text" />
                                </div>
                            </div>

                            <div className="side-baar-box-header">
                                <div className="box-header-row">
                                    <span>Picture</span>
                                    <div className="image-upload">
                                        <label>
                                            <input name="image" type="file" accept="image/*" />
                                            Add
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="side-baar-box-header">
                                <div className="box-header-row bubble-text">
                                    <input name="profile_url" placeholder="Social profile URL..." type="text" />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <input name="website_url" placeholder="Website URL..." type="text" />
                                </div>
                                <div className="box-header-row">
                                    <span>Logo</span>
                                    <div className="image-upload">
                                        <label>
                                            <input name="website_logo" type="file" accept="image/*" />
                                            Add
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </>
            )}

            {activeSettingElementsTab === "edit_testimonial" && (
                <div className="inner-box-layout">
                    {isLoading ? (
                        <div className="inner-loader-container">
                            <div className="inner-spinner"></div>
                            <p>Loading, please wait...</p>
                        </div>
                    ) : apiSingleTestimonial ? (
                        <form onSubmit={handleUpdateTestimonial}>
                            <span>
                                <button className="button-color" type="button" onClick={() => setActiveSettingElementsTab("main")}>
                                    <i className="fa fa-angle-left" aria-hidden="true"></i>Cancel
                                </button>
                                Edit Testimonial
                                <button type="submit" className="button-color button-done" disabled={isSubmitTestimonial}>
                                    {isSubmitTestimonial ? "Updating..." : "Update"}
                                </button>
                            </span>

                            <div className="side-baar-box-header">
                                <div className="box-header-row">
                                    <textarea
                                        name="description"
                                        placeholder="Testimonial Text...."
                                        className="testi-from"
                                        value={apiSingleTestimonial.description || ""}
                                        onChange={(e) =>
                                            setApiSingleTestimonial({ ...apiSingleTestimonial, description: e.target.value })
                                        }
                                    />
                                </div>
                                <div className="box-header-row">
                                    <span>Star Rating</span>
                                    <div className="start-rating">
                                        {[1, 2, 3, 4, 5].map((value) => (
                                            <i
                                                key={value}
                                                className={`fa fa-star ${value <= apiSingleTestimonial.rating ? "selected" : ""}`}
                                                aria-hidden="true"
                                                onClick={() =>
                                                    setApiSingleTestimonial({ ...apiSingleTestimonial, rating: value })
                                                }
                                                style={{
                                                    cursor: "pointer",
                                                    color: value <= apiSingleTestimonial.rating ? "gold" : "gray",
                                                }}
                                            ></i>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Hidden input field to send rating */}
                            <input type="hidden" name="rating" value={apiSingleTestimonial.rating} />

                            <div className="name-tile-row">Chat Window</div>
                            <div className="side-baar-box-header">
                                <div className="box-header-row bubble-text">
                                    <input
                                        name="name"
                                        placeholder="Name"
                                        type="text"
                                        value={apiSingleTestimonial.name || ""}
                                        onChange={(e) => setApiSingleTestimonial({ ...apiSingleTestimonial, name: e.target.value })}
                                    />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <input
                                        name="caption_name"
                                        placeholder="Caption"
                                        type="text"
                                        value={apiSingleTestimonial.caption_name || ""}
                                        onChange={(e) =>
                                            setApiSingleTestimonial({ ...apiSingleTestimonial, caption_name: e.target.value })
                                        }
                                    />
                                </div>
                            </div>

                            <div className="side-baar-box-header">
                                <div className="box-header-row">
                                    <span>Picture</span>
                                    <div className="image-upload">
                                    <input name="image" type="file" accept="image/*" />
                                    {apiSingleTestimonial.image && (
                                        <img src={apiSingleTestimonial.image} alt="Uploaded" width="50" height="50" />
                                    )}
                                    </div>
                                </div>
                            </div>

                            <div className="side-baar-box-header">
                                <div className="box-header-row bubble-text">
                                    <input
                                        name="profile_url"
                                        placeholder="Social profile URL..."
                                        type="text"
                                        value={apiSingleTestimonial.profile_url || ""}
                                        onChange={(e) =>
                                            setApiSingleTestimonial({ ...apiSingleTestimonial, profile_url: e.target.value })
                                        }
                                    />
                                </div>
                                <div className="box-header-row bubble-text">
                                    <input
                                        name="website_url"
                                        placeholder="Website URL..."
                                        type="text"
                                        value={apiSingleTestimonial.website_url || ""}
                                        onChange={(e) =>
                                            setApiSingleTestimonial({ ...apiSingleTestimonial, website_url: e.target.value })
                                        }
                                    />
                                </div>
                                <div className="box-header-row">
                                    <span>Logo</span>
                                    <div className="image-upload">
                                    <input name="website_logo" type="file" accept="image/*" />
                                    {apiSingleTestimonial.website_logo && (
                                        <img src={apiSingleTestimonial.website_logo} alt="Logo" width="50" height="50" />
                                    )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    ) : (
                        <p>No testimonial details. Please try again.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContentTab;
